import { PromptOptions } from "@/components/playground/prompt-caption";

export const Capability = {
  VisualQuery: "visual-query" as const,
  Caption: "caption" as const,
  ObjectDetection: "object-detection" as const,
  Pointing: "pointing" as const,
  Segmentation: "segmentation" as const,
  PointSegment: "point-segment" as const,
} as const;

export type Capability = typeof Capability[keyof typeof Capability];

export type InferenceStatus = "pending" | "streaming" | "done" | "error";

export type CaptionStreamHandlers = {
  onStart?: () => void;
  onChunk?: (caption: string) => void;
  onComplete?: (inference: InferenceType) => void;
  onError?: (error: unknown) => void;
};

// Helper function to parse Server-Timing header
// Format: "Server-Timing: total;dur=<milliseconds>"
const parseServerTiming = (response: Response): number | undefined => {
  const serverTiming = response.headers.get('Server-Timing');
  if (!serverTiming) {
    return undefined;
  }

  // Parse the Server-Timing header to extract duration
  // Format: "total;dur=123.45" or "total; dur=123.45"
  const match = serverTiming.match(/total;?\s*dur=([0-9.]+)/i);
  if (match && match[1]) {
    return parseFloat(match[1]);
  }

  return undefined;
};

// Calls expect a fully-formed inferenceUrl provided by the caller.

export async function callInference(
  inferenceUrl: string,
  capability: Capability,
  prompt: string,
  imageFile: File,
  uploadedImage: string,
  options?: PromptOptions,
  variant?: string,
  streamHandlers?: CaptionStreamHandlers
): Promise<InferenceType> {
  // Convert image to base64 
  const base64Image = await fileToBase64(imageFile);

  // Determine endpoint and request format based on capability
  let endpoint = "/query";
  let requestBody: Record<string, unknown> = {};
  let shouldStream = false;

  if (capability === Capability.VisualQuery) {
    endpoint = "/query";
    requestBody = {
      image_url: base64Image,
      question: prompt,
    };
    if (options?.reasoning) {
      requestBody.reasoning = true;
    }
    if (variant) {
      requestBody.variant = variant;
    }
  } else if (capability === Capability.Caption) {
    endpoint = "/caption";
    requestBody = {
      image_url: base64Image,
      length: options?.length || "short",
    };
    if (streamHandlers) {
      shouldStream = true;
      requestBody.stream = true;
    }
    if (variant) {
      requestBody.variant = variant;
    }
  } else if (capability === Capability.ObjectDetection) {
    endpoint = "/detect";
    requestBody = {
      image_url: base64Image,
      object: prompt || "all objects",
    };
    if (variant) {
      requestBody.variant = variant;
    }
  } else if (capability === Capability.Pointing) {
    endpoint = "/point";
    requestBody = {
      image_url: base64Image,
      object: prompt || "all objects",
    };
    if (variant) {
      requestBody.variant = variant;
    }
  } else if (capability === Capability.Segmentation || capability === Capability.PointSegment) {
    endpoint = "/segment";
    requestBody = {
      image_url: base64Image,
      object: prompt,
    };
    if (streamHandlers) {
      shouldStream = true;
      requestBody.stream = true;
    }
    if (variant) {
      requestBody.variant = variant;
    }
  }


  // Start timing right before the API request
  const startTime = performance.now();

  const response = await fetch(`${inferenceUrl}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  if (shouldStream) {
    try {
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Streaming is not supported in this environment.");
      }

      streamHandlers?.onStart?.();

      const decoder = new TextDecoder();
      let buffer = "";
      let aggregatedCaption = "";
      let reasoningData: InferenceDataType["reasoning"] | undefined;

      const flushLine = (line: string) => {
        const trimmed = line.trim();
        if (trimmed === "" || trimmed.startsWith(":")) {
          return;
        }
        if (!trimmed.startsWith("data:")) {
          return;
        }
        const payload = trimmed.slice(5).trim();
        if (payload === "" || payload === "[DONE]") {
          return;
        }

        let parsed: unknown;
        try {
          parsed = JSON.parse(payload);
        } catch (error) {
          console.error("Failed to parse caption stream payload", error);
          return;
        }

        if (typeof parsed !== "object" || parsed === null) {
          return;
        }

        const parsedRecord = parsed as Record<string, unknown>;
        const captionValue = parsedRecord.caption;
        const chunkValue = parsedRecord.chunk;

        if (typeof captionValue === "string" && captionValue.length > 0) {
          aggregatedCaption = captionValue;
          streamHandlers?.onChunk?.(aggregatedCaption);
        } else if (typeof chunkValue === "string" && chunkValue.length > 0) {
          aggregatedCaption += chunkValue;
          streamHandlers?.onChunk?.(aggregatedCaption);
        }

        const reasoningValue = parsedRecord.reasoning;
        if (
          reasoningValue &&
          typeof reasoningValue === "object" &&
          reasoningValue !== null
        ) {
          const candidate = reasoningValue as {
            text?: unknown;
            grounding?: unknown;
          };
          if (
            typeof candidate.text === "string" &&
            Array.isArray(candidate.grounding)
          ) {
            reasoningData = candidate as InferenceDataType["reasoning"];
          }
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          buffer += decoder.decode();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          flushLine(line);
        }
      }

      if (buffer.length > 0) {
        flushLine(buffer);
      }

      // Extract server-side duration from Server-Timing header
      // Falls back to client-side timing if header is not present
      const serverDuration = parseServerTiming(response);
      const endTime = performance.now();
      const clientDuration = endTime - startTime;
      const duration = serverDuration ?? clientDuration;

      const data: InferenceDataType = {
        answer: "",
        caption: aggregatedCaption,
        points: "",
        objects: "",
        ...(reasoningData ? { reasoning: reasoningData } : {}),
      };

      const inference = processInferenceResult(
        capability,
        data,
        uploadedImage!,
        prompt,
        duration,
        imageFile
      );

      streamHandlers?.onComplete?.(inference);

      return inference;
    } catch (error) {
      streamHandlers?.onError?.(error);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  const data: InferenceDataType = await response.json();

  // Extract server-side duration from Server-Timing header
  // Falls back to client-side timing if header is not present
  const serverDuration = parseServerTiming(response);
  const endTime = performance.now();
  const clientDuration = endTime - startTime;
  const duration = serverDuration ?? clientDuration;

  // Process the inference result using the utility function
  const newInference = processInferenceResult(
    capability,
    data,
    uploadedImage!,
    prompt,
    duration,
    imageFile // Pass the image file for code generation
  );

  return newInference;
}

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};



export async function fetchSuggestedQueries(
  inferenceUrl: string,
  capability: Capability,
  imageData: File
): Promise<string[]> {


  // Get base64 image
  const base64Image = await fileToBase64(imageData);

  // Determine prompt template based on capability
  let prompt = "";
  if (capability === Capability.VisualQuery) {
    prompt = `Analyze this image and return a JSON list of 3 specific, interesting questions that would help understand the image content. Focus on:
- What is happening in the scene?
- What are the key objects or people?
- What might be the context or purpose?
Format as: ["question 1", "question 2", "question 3"]`;
  } else if (capability === Capability.ObjectDetection) {
    prompt = `Look at this image and return a JSON list of 3 specific objects that could be detected. Focus on:
- Main objects in the scene
- Objects that stand out
- Objects that would be useful to identify
Format as: ["object 1", "object 2", "object 3"]`;
  } else if (capability === Capability.Pointing) {
    prompt = `Examine this image and return a JSON list of 3 specific things someone might want to point to. Focus on:
- Important details or features
- Objects that need attention
- Specific locations or items
Format as: ["item 1", "item 2", "item 3"]`;
  } else if (capability === Capability.Segmentation || capability === Capability.PointSegment) {
    prompt = `Inspect this image and return a JSON list of 3 short referring expressions for distinct, segmentable items. Each entry should name a specific instance (e.g., "the blue mug on the left", "the front wheel of the red bike"). Format as: ["item 1", "item 2", "item 3"]`;
  }

  // Create the request body
  const requestBody = {
    image_url: base64Image,
    question: prompt,
  };



  const response = await fetch(`${inferenceUrl}/query`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });



  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  try {
    // Try to parse the answer as JSON
    const jsonList = JSON.parse(data.answer);

    // Check if it's an array and return the first three items
    if (Array.isArray(jsonList)) {
      return jsonList.slice(0, 3);
    }
    return [];
  } catch (e) {
    console.error("Failed to parse suggested queries as JSON:", data.answer, e);
    return [];
  }
}

export type SegmentResult = {
  bbox: { x_min: number; y_min: number; x_max: number; y_max: number };
  path: string;
}

export type InferenceType = {
  id: number;
  image: string;
  imageFile?: File; // Add image file for code generation
  result:
  | string
  | { x: number; y: number }[]
  | { x_min: number; y_min: number; x_max: number; y_max: number }[];
  capability: Capability;
  query: string;
  captionLength?: string;
  reasoning?: {
    text: string;
    grounding: Array<{
      start_idx: number;
      end_idx: number;
      points: Array<[number, number]>;
    }>;
  };
  duration?: number; // Added duration field
  status?: InferenceStatus;
  segmentResult?: SegmentResult;
  segmentResults?: Array<SegmentResult | null>;
};

export type InferenceDataType = {
  answer: string;
  caption: string;
  points: string;
  objects: string;
  segmentResult?: SegmentResult;
  segmentResults?: Array<SegmentResult | null>;
  reasoning?: {
    text: string;
    grounding: Array<{
      start_idx: number;
      end_idx: number;
      points: Array<[number, number]>;
    }>;
  };
};

export function processInferenceResult(
  capability: Capability,
  data: InferenceDataType,
  uploadedImage: string,
  prompt: string,
  duration?: number, // Added duration parameter
  imageFile?: File // Add image file parameter
): InferenceType {
  let resultData = "";
  if (capability === Capability.VisualQuery) {
    resultData = data.answer || "";
  } else if (capability === Capability.Caption) {
    resultData = data.caption || "";
  } else if (capability === Capability.ObjectDetection) {
    resultData = data.objects || "";
  } else if (capability === Capability.Pointing) {
    resultData = data.points || "";
  } else if ((capability === Capability.Segmentation || capability === Capability.PointSegment) && data.segmentResult) {
    resultData = JSON.stringify(data.segmentResult);
  } else if (capability === Capability.PointSegment && data.segmentResults) {
    resultData = JSON.stringify(data.segmentResults);
  }

  const inference: InferenceType = {
    id: Date.now(),
    image: uploadedImage,
    imageFile: imageFile, // Add image file to inference object
    result: resultData,
    capability: capability,
    query: prompt,
    status: "done",
  };

  if (data.reasoning) {
    inference.reasoning = data.reasoning;
  }

  if (data.segmentResult) {
    inference.segmentResult = data.segmentResult;
  }
  if (data.segmentResults) {
    inference.segmentResults = data.segmentResults;
  }

  if (duration !== undefined) { // Added duration to inference object
    inference.duration = duration;
  }

  return inference;
}

// Helper function to generate code examples for each inference request
export function generateCodeExample(
  capability: Capability,
  prompt: string,
  imageFile: File,
  options?: PromptOptions
): { python: string; javascript: string; curl: string } {
  const fileName = imageFile.name || "image.jpg";
  const fileExtension = fileName.split('.').pop() || "jpg";
  const mimeType = fileExtension === "png" ? "image/png" : "image/jpeg";

  // Base64 placeholder - in real usage this would be the actual base64 string
  const base64Placeholder = "data:" + mimeType + ";base64,<BASE64-STRING>";

  let pythonCode = "";
  let javascriptCode = "";
  let curlCode = "";

  if (capability === Capability.VisualQuery) {
    // Python
    pythonCode = `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load an image
image = Image.open("${fileName}")

# Ask a question
result = model.query(image, "${prompt.replace(/"/g, '\\"')}")
answer = result["answer"]
request_id = result["request_id"]
print(f"Answer: {answer}")
print(f"Request ID: {request_id}")`;

    // JavaScript
    javascriptCode = `import fs from "fs";

const model = new vl({ apiKey: "your-api-key" });
const image = fs.readFileSync("${fileName}");

async function main() {
  const result = await model.query({
    image,
    question: "${prompt.replace(/"/g, '\\"')}"
  });

}

main();`;

    // cURL
    curlCode = `# Replace \${process.env.MOONDREAM_API_KEY} with your API key from https://moondream.ai/c/cloud
curl --location 'https://api.moondream.ai/v1/query' \\
--header 'X-Moondream-Auth: \${process.env.MOONDREAM_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '{
    "image_url": "${base64Placeholder}",
    "question": "${prompt.replace(/"/g, '\\"')}",
    "stream": false
}'`;

  } else if (capability === Capability.Caption) {
    const length = options?.length || "normal";

    // Python
    pythonCode = `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load an image
image = Image.open("${fileName}")

# Generate caption
result = model.caption(image, length="${length}")
caption = result["caption"]
request_id = result["request_id"]
print(f"Caption: {caption}")
print(f"Request ID: {request_id}")`;

    // JavaScript
    javascriptCode = `import fs from "fs";

const model = new vl({ apiKey: "your-api-key" });
const image = fs.readFileSync("${fileName}");

async function main() {
  const result = await model.caption({
    image,
    length: "${length}"
  });

}

main();`;

    // cURL
    curlCode = `# Replace \${process.env.MOONDREAM_API_KEY} with your API key from https://moondream.ai/c/cloud
curl --location 'https://api.moondream.ai/v1/caption' \\
--header 'X-Moondream-Auth: \${process.env.MOONDREAM_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '{
    "image_url": "${base64Placeholder}",
    "length": "${length}",
    "stream": false
}'`;

  } else if (capability === Capability.ObjectDetection) {
    const object = prompt || "all objects";

    // Python
    pythonCode = `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load an image
image = Image.open("${fileName}")

# Detect objects
result = model.detect(image, "${object.replace(/"/g, '\\"')}")
objects = result["objects"]
request_id = result["request_id"]
print(f"Detected objects: {objects}")
print(f"Request ID: {request_id}")`;

    // JavaScript
    javascriptCode = `import fs from "fs";

const model = new vl({ apiKey: "your-api-key" });
const image = fs.readFileSync("${fileName}");

async function main() {
  const result = await model.detect({
    image,
    object: "${object.replace(/"/g, '\\"')}"
  });

}

main();`;

    // cURL
    curlCode = `# Replace \${process.env.MOONDREAM_API_KEY} with your API key from https://moondream.ai/c/cloud
curl --location 'https://api.moondream.ai/v1/detect' \\
--header 'X-Moondream-Auth: \${process.env.MOONDREAM_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '{
    "image_url": "${base64Placeholder}",
    "object": "${object.replace(/"/g, '\\"')}"
}'`;

  } else if (capability === Capability.Pointing) {
    const object = prompt || "all objects";

    // Python
    pythonCode = `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load an image
image = Image.open("${fileName}")

# Point to objects
result = model.point(image, "${object.replace(/"/g, '\\"')}")
points = result["points"]
request_id = result["request_id"]
print(f"Points: {points}")
print(f"Request ID: {request_id}")`;

    // JavaScript
    javascriptCode = `import fs from "fs";

const model = new vl({ apiKey: "your-api-key" });
const image = fs.readFileSync("${fileName}");

async function main() {
  const result = await model.point({
    image,
    object: "${object.replace(/"/g, '\\"')}"
  });

}

main();`;

    // cURL
    curlCode = `# Replace \${process.env.MOONDREAM_API_KEY} with your API key from https://moondream.ai/c/cloud
curl --location 'https://api.moondream.ai/v1/point' \\
--header 'X-Moondream-Auth: \${process.env.MOONDREAM_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '{
    "image_url": "${base64Placeholder}",
    "object": "${object.replace(/"/g, '\\"')}"
}'`;
  } else if (capability === Capability.Segmentation) {
    const object = prompt || "";

    // SVG rendering comment for all languages
    const svgRenderingComment = `
# The result contains a bounding box and SVG path for the segment:
# {
#   "path": "M 0 0.76 L 0 0.32 L 0.03 0.31 ...",
#   "bbox": { "x_min": 0.0, "y_min": 0.0, "x_max": 0.78, "y_max": 0.99 }
# }
#
# To render the segment on top of an image (HTML example):
# <div style="position: relative; width: 800px; height: 600px;">
#   <img src="your-image.jpg" style="width: 100%; height: 100%;" />
#   <svg
#     viewBox="0 0 1 1"
#     preserveAspectRatio="none"
#     style="
#       position: absolute;
#       left: calc(bbox.x_min * 100%);
#       top: calc(bbox.y_min * 100%);
#       width: calc((bbox.x_max - bbox.x_min) * 100%);
#       height: calc((bbox.y_max - bbox.y_min) * 100%);
#     "
#   >
#     <path d={result.path} fill="rgba(255,0,0,0.3)" stroke="red" stroke-width="0.01"/>
#   </svg>
# </div>`;

    const jsRenderingComment = `
// The result contains a bounding box and SVG path for the segment:
// {
//   "path": "M 0 0.76 L 0 0.32 L 0.03 0.31 ...",
//   "bbox": { "x_min": 0.0, "y_min": 0.0, "x_max": 0.78, "y_max": 0.99 }
// }
//
// To render the segment on top of an image (HTML example):
// <div style="position: relative; width: 800px; height: 600px;">
//   <img src="your-image.jpg" style="width: 100%; height: 100%;" />
//   <svg
//     viewBox="0 0 1 1"
//     preserveAspectRatio="none"
//     style="
//       position: absolute;
//       left: calc(bbox.x_min * 100%);
//       top: calc(bbox.y_min * 100%);
//       width: calc((bbox.x_max - bbox.x_min) * 100%);
//       height: calc((bbox.y_max - bbox.y_min) * 100%);
//     "
//   >
//     <path d={result.path} fill="rgba(255,0,0,0.3)" stroke="red" stroke-width="0.01"/>
//   </svg>
// </div>`;

    pythonCode = `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load an image
image = Image.open("${fileName}")

# Segment an object
result = model.segment(image, "${object.replace(/"/g, '\\"')}")
svg_path = result["path"]
bbox = result["bbox"]
print(f"SVG Path: {svg_path}")
print(f"Bounding Box: {bbox}")
${svgRenderingComment}`;

    javascriptCode = `import fs from "fs";

const model = new vl({ apiKey: "your-api-key" });
const image = fs.readFileSync("${fileName}");

async function main() {
  const result = await model.segment({
    image,
    object: "${object.replace(/"/g, '\\"')}"
  });
  const { path, bbox } = result;
  console.log("SVG Path:", path);
  console.log("Bounding Box:", bbox);
}

main();
${jsRenderingComment}`;

    curlCode = `# Replace \${process.env.MOONDREAM_API_KEY} with your API key from https://moondream.ai/c/cloud
curl --location 'https://api.moondream.ai/v1/segment' \\
--header 'X-Moondream-Auth: \${process.env.MOONDREAM_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '{
    "image_url": "${base64Placeholder}",
    "object": "${object.replace(/"/g, '\\"')}"
}'
${svgRenderingComment}`;

  } else if (capability === Capability.PointSegment) {
    const object = prompt || "";

    // SVG rendering comment for all languages
    const svgRenderingComment = `
# Each segment result contains a bounding box and SVG path:
# {
#   "path": "M 0 0.76 L 0 0.32 L 0.03 0.31 ...",
#   "bbox": { "x_min": 0.0, "y_min": 0.0, "x_max": 0.78, "y_max": 0.99 }
# }
#
# To render the segment on top of an image (HTML example):
# <div style="position: relative; width: 800px; height: 600px;">
#   <img src="your-image.jpg" style="width: 100%; height: 100%;" />
#   <svg
#     viewBox="0 0 1 1"
#     preserveAspectRatio="none"
#     style="
#       position: absolute;
#       left: calc(bbox.x_min * 100%);
#       top: calc(bbox.y_min * 100%);
#       width: calc((bbox.x_max - bbox.x_min) * 100%);
#       height: calc((bbox.y_max - bbox.y_min) * 100%);
#     "
#   >
#     <path d={result.path} fill="rgba(255,0,0,0.3)" stroke="red" stroke-width="0.01"/>
#   </svg>
# </div>`;

    const jsRenderingComment = `
// Each segment result contains a bounding box and SVG path:
// {
//   "path": "M 0 0.76 L 0 0.32 L 0.03 0.31 ...",
//   "bbox": { "x_min": 0.0, "y_min": 0.0, "x_max": 0.78, "y_max": 0.99 }
// }
//
// To render the segment on top of an image (HTML example):
// <div style="position: relative; width: 800px; height: 600px;">
//   <img src="your-image.jpg" style="width: 100%; height: 100%;" />
//   <svg
//     viewBox="0 0 1 1"
//     preserveAspectRatio="none"
//     style="
//       position: absolute;
//       left: calc(bbox.x_min * 100%);
//       top: calc(bbox.y_min * 100%);
//       width: calc((bbox.x_max - bbox.x_min) * 100%);
//       height: calc((bbox.y_max - bbox.y_min) * 100%);
//     "
//   >
//     <path d={result.path} fill="rgba(255,0,0,0.3)" stroke="red" stroke-width="0.01"/>
//   </svg>
// </div>`;

    pythonCode = `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load an image
image = Image.open("${fileName}")

# Step 1: Find points for all matching objects
point_result = model.point(image, "${object.replace(/"/g, '\\"')}")
points = point_result["points"]
print(f"Found {len(points)} point(s)")

# Step 2: Segment each point individually
segments = []
for point in points:
    result = model.segment(
        image,
        "${object.replace(/"/g, '\\"')}",
        spatial_refs=[[point["x"], point["y"]]]
    )
    segments.append({
        "path": result["path"],
        "bbox": result["bbox"]
    })

print(f"Segments: {segments}")
${svgRenderingComment}`;

    javascriptCode = `import fs from "fs";

const model = new vl({ apiKey: "your-api-key" });
const image = fs.readFileSync("${fileName}");

async function main() {
  // Step 1: Find points for all matching objects
  const pointResult = await model.point({
    image,
    object: "${object.replace(/"/g, '\\"')}"
  });
  const points = pointResult.points;
  console.log(\`Found \${points.length} point(s)\`);

  // Step 2: Segment each point individually
  const segments = await Promise.all(
    points.map(async (point) => {
      const result = await model.segment({
        image,
        object: "${object.replace(/"/g, '\\"')}",
        spatial_refs: [[point.x, point.y]]
      });
      return { path: result.path, bbox: result.bbox };
    })
  );

  console.log("Segments:", segments);
}

main();
${jsRenderingComment}`;

    curlCode = `# Multi-object segmentation requires two API calls:
# Step 1: Get points for all matching objects
# Replace \${process.env.MOONDREAM_API_KEY} with your API key from https://moondream.ai/c/cloud

curl --location 'https://api.moondream.ai/v1/point' \\
--header 'X-Moondream-Auth: \${process.env.MOONDREAM_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '{
    "image_url": "${base64Placeholder}",
    "object": "${object.replace(/"/g, '\\"')}"
}'

# Step 2: For each point returned, call segment with spatial_refs
# Example for a point at coordinates (0.5, 0.3):

curl --location 'https://api.moondream.ai/v1/segment' \\
--header 'X-Moondream-Auth: \${process.env.MOONDREAM_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '{
    "image_url": "${base64Placeholder}",
    "object": "${object.replace(/"/g, '\\"')}",
    "spatial_refs": [[0.5, 0.3]]
}'
${svgRenderingComment}`;
  }

  return { python: pythonCode, javascript: javascriptCode, curl: curlCode };
}
